import { createContext, forwardRef, useCallback, useContext, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Icon } from '../../icons';
// Registers every entity's statuses (side effect) so direct imports of this module resolve like the barrel.
import './statuses';
import { resolveStatus, type StatusTone } from './registry';
import type { StatusEntity } from './statuses';

export type StatusBadgeVariant = 'soft' | 'outline' | 'solid';
export type StatusBadgeSize = 'sm' | 'md';

/** Optional label resolver so the app can localise `labelKey`s without the library depending on i18n. */
export type StatusLabelResolver = (labelKey: string | undefined, fallback: string) => string;
const StatusLabelContext = createContext<StatusLabelResolver>((_, fallback) => fallback);
export function StatusLabelProvider({ resolve, children }: { resolve: StatusLabelResolver; children: ReactNode }) {
  return <StatusLabelContext.Provider value={resolve}>{children}</StatusLabelContext.Provider>;
}

export interface StatusBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Backend value, e.g. `IN_TRANSIT`. */
  status: string;
  /** Entity type in the status registry, e.g. `shipment`. Falls back to the generic map. */
  entity?: StatusEntity | (string & {});
  variant?: StatusBadgeVariant;
  size?: StatusBadgeSize;
  /** Overrides the registry label. */
  label?: ReactNode;
  /** Hides the leading dot / icon. */
  hideIndicator?: boolean;
}

const soft: Record<StatusTone, string> = {
  info: 'bg-info-bg text-info-fg',
  success: 'bg-success-bg text-success-fg',
  warning: 'bg-warning-bg text-warning-fg',
  danger: 'bg-danger-bg text-danger-fg',
  neutral: 'bg-neutral-bg text-neutral-fg',
  violet: 'bg-ai-bg text-ai-fg',
};
const outline: Record<StatusTone, string> = {
  info: 'border border-info-border text-info-fg',
  success: 'border border-success-border text-success-fg',
  warning: 'border border-warning-border text-warning-fg',
  danger: 'border border-danger-border text-danger-fg',
  neutral: 'border border-neutral-border text-neutral-fg',
  violet: 'border border-ai-border text-ai-fg',
};
const solid: Record<StatusTone, string> = {
  info: 'bg-info-fg text-fg-inverse',
  success: 'bg-success-fg text-fg-inverse',
  warning: 'bg-warning-fg text-fg-inverse',
  danger: 'bg-danger-fg text-fg-inverse',
  neutral: 'bg-neutral-fg text-fg-inverse',
  violet: 'bg-ai-fg text-fg-inverse',
};
const dot: Record<StatusTone, string> = {
  info: 'bg-info',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  neutral: 'bg-neutral',
  violet: 'bg-ai',
};
export const statusToneClasses = { soft, outline, solid, dot };

/**
 * The only status pill in the product (brief §5.4): registry-driven label + tone, 6px leading dot, 12px text,
 * 24px tall (20px compact via `sm`). Colour is never the only signal — the text label is always present.
 */
/** A status's label in the viewer's language, outside a badge (the journey spine's steps). */
export function useStatusLabel(): (entity: string | undefined, value: string) => string {
  const resolve = useContext(StatusLabelContext);
  return useCallback((entity, value) => {
    const entry = resolveStatus(entity, value);
    return String(resolve(entry.labelKey, entry.label));
  }, [resolve]);
}

export const StatusBadge = forwardRef<HTMLSpanElement, StatusBadgeProps>(function StatusBadge(
  { status, entity, variant = 'soft', size = 'md', label, hideIndicator, className, ...rest },
  ref,
) {
  const resolve = useContext(StatusLabelContext);
  const entry = resolveStatus(entity, status);
  const text = label ?? resolve(entry.labelKey, entry.label);
  const showDot = !hideIndicator && entry.dotStyle !== 'none' && !entry.icon;
  const showIcon = !hideIndicator && !!entry.icon;
  const dotClass = variant === 'solid' ? 'bg-fg-inverse/80' : dot[entry.tone];
  return (
    <span
      ref={ref}
      data-status={entry.value}
      data-tone={entry.tone}
      data-known={entry.known || undefined}
      className={cn(
        'zo-status-badge inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-full font-strong',
        size === 'sm' ? 'h-5 px-2 type-caption' : 'h-6 px-2.5 type-caption',
        variant === 'soft' && soft[entry.tone],
        variant === 'outline' && outline[entry.tone],
        variant === 'solid' && solid[entry.tone],
        className,
      )}
      {...rest}
    >
      {showDot ? <span aria-hidden className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dotClass)} /> : null}
      {showIcon && entry.icon ? <Icon name={entry.icon} size={12} aria-hidden="true" /> : null}
      <span className={cn('truncate', entry.strike && 'line-through')}>{text}</span>
    </span>
  );
});
